import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { hasAnyPermission, hasPermission } from "../../utils/permissions";
import LedgerAccountModal from "./LedgerAccountModal";
import type {
  ApprovalThreshold,
  BankAccount,
  FinanceDepartment,
  FinanceParty,
  FrontOfficeSettings,
  RestaurantSettings,
  InventorySettings,
  GlAccount,
  PostingQueueItem,
  PayrollSettings,
} from "../../types";

const { Title, Text } = Typography;

const TYPE_COLOR: Record<string, string> = {
  asset: "blue",
  liability: "volcano",
  equity: "purple",
  income: "green",
  expense: "orange",
};

/** Round money-shaped derived figures the way the server rounds them. */
const round2 = (value: number) => Math.round((Number(value) || 0) * 100) / 100;

interface EditableBand {
  max: number | null;
  rate: number;
}

/**
 * Rebuild the IRD bands from their two editable facts — where each band ends
 * and what it charges — and re-derive everything else.
 *
 * `from` is by definition the top of the band below it and `constant` is the
 * tax the bands below have already earned, so both follow from the thresholds
 * rather than being typed in. Editing one number cannot desynchronise them.
 */
function rebuildBands(bands: EditableBand[]): Array<{ max: number | null; from: number; rate: number; constant: number }> {
  let from = 0;
  let constant = 0;
  return bands.map((band) => {
    const out = { from, max: band.max, rate: band.rate, constant };
    if (band.max !== null) {
      constant = round2(constant + band.rate * (band.max - from));
      from = band.max;
    }
    return out;
  });
}

/**
 * Finance master data.
 *
 * Each modal owns its Form instance and is initialised from `initialValues`
 * (the Form is remounted per open via destroyOnClose), so editing one record
 * never leaks values into the next one opened.
 */
const FinanceSettingsPage: React.FC = () => {
  const canManage = hasPermission("finance:accounts");
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [departments, setDepartments] = useState<FinanceDepartment[]>([]);
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [parties, setParties] = useState<FinanceParty[]>([]);
  const [thresholds, setThresholds] = useState<ApprovalThreshold[]>([]);
  const [frontOffice, setFrontOffice] = useState<FrontOfficeSettings | null>(null);
  const [restaurant, setRestaurant] = useState<RestaurantSettings | null>(null);
  const [inventory, setInventory] = useState<InventorySettings | null>(null);
  const [queue, setQueue] = useState<{ pending: number; failed: number; items: PostingQueueItem[] } | null>(
    null,
  );

  /**
   * Payroll settings live here but are NOT finance settings: they are guarded
   * by payroll:edit (or settings:edit) because they change what every future
   * run withholds. A cashier who may edit the chart of accounts does not
   * therefore get to change the EPF rate.
   */
  const canPayroll = hasAnyPermission("payroll:edit", "settings:edit");
  const seesPayroll = canPayroll || hasPermission("payroll:view");
  const [payroll, setPayroll] = useState<PayrollSettings | null>(null);
  const [rateEe, setRateEe] = useState(0);
  const [rateEr, setRateEr] = useState(0);
  const [rateEtf, setRateEtf] = useState(0);
  const [payrollBands, setPayrollBands] = useState<EditableBand[]>([]);
  const [savingPayroll, setSavingPayroll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [bankForm] = Form.useForm();
  const [partyForm] = Form.useForm();
  const [departmentForm] = Form.useForm();
  const [thresholdForm] = Form.useForm();

  const [accountModal, setAccountModal] = useState<{ open: boolean; editing?: GlAccount }>({ open: false });
  const [bankModal, setBankModal] = useState<{ open: boolean; editing?: BankAccount }>({ open: false });
  const [partyModal, setPartyModal] = useState<{ open: boolean; editing?: FinanceParty }>({ open: false });
  const [departmentModal, setDepartmentModal] = useState(false);
  const [thresholdModal, setThresholdModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [acc, dep, bank, party, settings, fo, rest, inv, q] = await Promise.all([
        financeService.getAccounts(true),
        financeService.getDepartments(),
        financeService.getBankAccounts(true),
        financeService.getParties(),
        financeService.getApprovalSettings(),
        financeService.getFrontOfficeSettings(),
        financeService.getRestaurantSettings(),
        financeService.getInventorySettings(),
        financeService.getPostingQueue(),
      ]);
      setAccounts(acc);
      setDepartments(dep);
      setBanks(bank);
      setParties(party);
      setThresholds(settings.thresholds);
      setFrontOffice(fo);
      setRestaurant(rest);
      setInventory(inv);
      setQueue(q);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Failed to load finance settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Statutory rates are fetched only for someone allowed to see them. A
   * cashier holding `finance:accounts` but no payroll permission must not be
   * able to read the EPF rate off this page — 403 is the correct answer, so
   * do not ask the question in the first place.
   */
  useEffect(() => {
    if (!seesPayroll) return;
    financeService
      .getPayrollSettings()
      .then((settings) => {
        setPayroll(settings);
        setRateEe(settings.epf_employee_rate);
        setRateEr(settings.epf_employer_rate);
        setRateEtf(settings.etf_employer_rate);
        setPayrollBands(settings.apit_bands.map((b) => ({ max: b.max, rate: b.rate })));
      })
      .catch(() => setPayroll(null));
  }, [seesPayroll]);

  const savePayroll = async () => {
    setSavingPayroll(true);
    try {
      const saved = await financeService.savePayrollSettings({
        epf_employee_rate: rateEe,
        epf_employer_rate: rateEr,
        etf_employer_rate: rateEtf,
        apit_bands: rebuildBands(payrollBands),
      });
      setPayroll(saved);
      setPayrollBands(saved.apit_bands.map((b) => ({ max: b.max, rate: b.rate })));
      message.success("Payroll statutory settings saved");
    } catch (e: any) {
      message.error(e?.message || "Failed to save payroll settings");
    } finally {
      setSavingPayroll(false);
    }
  };

  const act = async (label: string, action: () => Promise<any>) => {
    try {
      await action();
      message.success(label);
      await load();
    } catch (e: any) {
      message.error(e?.message || `${label} failed`);
    }
  };

  /* ---------------- Chart of accounts ---------------- */

  const accountColumns = [
    { title: "Code", dataIndex: "code", key: "code", width: 90, render: (v: string) => <Text code>{v}</Text> },
    { title: "Account", dataIndex: "name", key: "name", ellipsis: true },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 110,
      render: (v: string) => <Tag color={TYPE_COLOR[v]}>{v}</Tag>,
    },
    {
      title: "Parent",
      dataIndex: "parent_code",
      key: "parent_code",
      width: 90,
      render: (v: string | null) => v || "—",
    },
    {
      title: "Posting",
      dataIndex: "is_posting",
      key: "is_posting",
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">postable</Tag> : <Tag>header</Tag>),
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (v: boolean) => (v ? <Tag color="green">yes</Tag> : <Tag color="default">no</Tag>),
    },
    ...(canManage
      ? [
          {
            title: "",
            key: "actions",
            width: 90,
            render: (_: any, row: GlAccount) => (
              <Button size="small" onClick={() => setAccountModal({ open: true, editing: row })}>
                Edit
              </Button>
            ),
          },
        ]
      : []),
  ];

  const submitBank = async () => {
    const values = await bankForm.validateFields();
    await act(bankModal.editing ? "Bank account updated" : "Bank account created", () =>
      bankModal.editing
        ? financeService.updateBankAccount(bankModal.editing.id, values)
        : financeService.createBankAccount(values),
    );
    setBankModal({ open: false });
  };

  const submitParty = async () => {
    const values = await partyForm.validateFields();
    await act(partyModal.editing ? "Party updated" : "Party created", () =>
      partyModal.editing
        ? financeService.updateParty(partyModal.editing.id, values)
        : financeService.createParty(values),
    );
    setPartyModal({ open: false });
  };

  const submitDepartment = async () => {
    const values = await departmentForm.validateFields();
    await act("Department created", () => financeService.createDepartment(values));
    setDepartmentModal(false);
  };

  const submitThresholds = async () => {
    const values = await thresholdForm.validateFields();
    await act("Approval thresholds saved", () =>
      financeService.saveApprovalSettings(
        (values.rows as Array<{ max: number | null; approvals: number }>).map((r) => ({
          max: r.max === null || r.max === undefined ? null : Number(r.max),
          approvals: Number(r.approvals),
        })),
      ),
    );
    setThresholdModal(false);
  };

  /** Moving the cutover re-baselines reconciliation and every room KPI. */
  const saveCutover = (value: any) => {
    if (!value) return;
    act("Front-office cutover saved", () =>
      financeService.saveFrontOfficeSettings(value.format("YYYY-MM-DD")),
    );
  };

  /**
   * The POS cutover gates only the non-folio half — walk-in POS and walk-in
   * laundry. A booking-linked order rides the folio and is already covered by
   * the front-office cutover above.
   */
  const saveRestaurantCutover = (value: any) => {
    if (!value) return;
    act("Restaurant cutover saved", () =>
      financeService.saveRestaurantSettings(value.format("YYYY-MM-DD")),
    );
  };

  const saveInventoryCutover = (value: any) => {
    if (!value) return;
    act("Inventory cutover saved", () =>
      financeService.saveInventorySettings(value.format("YYYY-MM-DD")),
    );
  };

  /** The bounds and constants are derived from the two editable facts. */
  const derivedBands = rebuildBands(payrollBands);

  const tabItems = [
    {
      key: "accounts",
      label: "Chart of accounts",
      children: (
        <>
          {canManage && (
            <Space style={{ marginBottom: 12 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAccountModal({ open: true })}
              >
                New account
              </Button>
            </Space>
          )}
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={accounts}
            columns={accountColumns}
            pagination={false}
            scroll={{ x: 780 }}
          />
        </>
      ),
    },
    {
      key: "departments",
      label: "Departments",
      children: (
        <>
          {canManage && (
            <Space style={{ marginBottom: 12 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setDepartmentModal(true)}>
                New department
              </Button>
            </Space>
          )}
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={departments}
            pagination={false}
            columns={[
              { title: "Code", dataIndex: "code", width: 200, render: (v: string) => <Text code>{v}</Text> },
              { title: "Name", dataIndex: "name" },
              { title: "Order", dataIndex: "sort_order", width: 90, align: "right" as const },
              {
                title: "Active",
                dataIndex: "is_active",
                width: 90,
                render: (v: boolean) => (v ? <Tag color="green">yes</Tag> : <Tag>no</Tag>),
              },
            ]}
          />
        </>
      ),
    },
    {
      key: "banks",
      label: "Cash & bank",
      children: (
        <>
          {canManage && (
            <Space style={{ marginBottom: 12 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setBankModal({ open: true })}>
                New account
              </Button>
            </Space>
          )}
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={banks}
            pagination={false}
            scroll={{ x: 820 }}
            columns={[
              { title: "Name", dataIndex: "name" },
              { title: "Type", dataIndex: "type", width: 100, render: (v: string) => <Tag>{v}</Tag> },
              {
                title: "GL account",
                key: "gl",
                width: 200,
                ellipsis: true,
                render: (_: any, r: BankAccount) => (
                  <>
                    <Text code>{r.account_code}</Text> {r.account_name}
                  </>
                ),
              },
              {
                title: "Account no.",
                dataIndex: "account_no",
                width: 150,
                render: (v: string | null) => v || "—",
              },
              {
                title: "Opening",
                dataIndex: "opening_balance",
                width: 140,
                align: "right" as const,
                render: (v: number) => formatLkr(Number(v || 0)),
              },
              {
                title: "Default",
                dataIndex: "is_default",
                width: 90,
                render: (v: boolean) => (v ? <Tag color="gold">default</Tag> : "—"),
              },
              ...(canManage
                ? [
                    {
                      title: "",
                      key: "actions",
                      width: 90,
                      render: (_: any, row: BankAccount) => (
                        <Button size="small" onClick={() => setBankModal({ open: true, editing: row })}>
                          Edit
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </>
      ),
    },
    {
      key: "parties",
      label: "Customers & suppliers",
      children: (
        <>
          {canManage && (
            <Space style={{ marginBottom: 12 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setPartyModal({ open: true })}>
                New party
              </Button>
            </Space>
          )}
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={parties}
            pagination={false}
            columns={[
              { title: "Name", dataIndex: "name" },
              { title: "Type", dataIndex: "type", width: 120, render: (v: string) => <Tag>{v}</Tag> },
              { title: "Phone", dataIndex: "phone", width: 150, render: (v: string | null) => v || "—", ellipsis: true },
              { title: "Email", dataIndex: "email", width: 220, render: (v: string | null) => v || "—", ellipsis: true },
              ...(canManage
                ? [
                    {
                      title: "",
                      key: "actions",
                      width: 90,
                      render: (_: any, row: FinanceParty) => (
                        <Button size="small" onClick={() => setPartyModal({ open: true, editing: row })}>
                          Edit
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </>
      ),
    },
    {
      key: "thresholds",
      label: "Approval thresholds",
      children: (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            title="How many people must sign"
            description="Every approval must come from a different holder of finance:approve, and the creator can never approve their own voucher."
          />
          {canManage && (
            <Space style={{ marginBottom: 12 }}>
              <Button type="primary" onClick={() => setThresholdModal(true)}>
                Edit thresholds
              </Button>
            </Space>
          )}
          <Table
            rowKey={(_r, i) => String(i)}
            size="small"
            loading={loading}
            dataSource={thresholds}
            pagination={false}
            columns={[
              {
                title: "Voucher value",
                key: "band",
                render: (_: any, t: ApprovalThreshold, i: number) => {
                  const lower = i === 0 ? 0 : thresholds[i - 1].max ?? 0;
                  return t.max === null
                    ? `Above ${formatLkr(lower)}`
                    : `${formatLkr(lower)} – ${formatLkr(t.max)}`;
                },
              },
              {
                title: "Approvals required",
                dataIndex: "approvals",
                width: 200,
                render: (v: number) => <Tag color="processing">{v}</Tag>,
              },
            ]}
          />
        </>
      ),
    },
    {
      key: "front-office",
      label: "Front office",
      children: (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            title="The ledger starts at the cutover date"
            description="Phase 2 does not back-fill folio history. Reconciliation and the occupancy / ADR / RevPAR KPIs clamp to this date, so a gap before it is never reported as a variance. Charges dated on or after it post to the general ledger; a closed business date queues them instead of failing check-in or check-out."
          />

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={8}>
              <Card size="small" title="Ledger cutover" loading={loading}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <DatePicker
                    style={{ width: "100%" }}
                    disabled={!canManage}
                    value={frontOffice ? dayjs(frontOffice.cutover_date) : null}
                    onChange={saveCutover}
                    allowClear={false}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {frontOffice
                      ? `${frontOffice.closed_days} business date(s) closed since the cutover`
                      : ""}
                  </Text>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" title="Waiting to post" loading={loading}>
                <Statistic
                  value={queue?.pending ?? frontOffice?.pending_postings ?? 0}
                  valueStyle={{ color: (queue?.pending ?? 0) > 0 ? "#faad14" : undefined }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  deferred behind a closed business date
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" title="Held back" loading={loading}>
                <Statistic
                  value={queue?.failed ?? frontOffice?.failed_postings ?? 0}
                  valueStyle={{ color: (queue?.failed ?? 0) > 0 ? "#a5171f" : undefined }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  tender with no cash / bank / card account mapping
                </Text>
              </Card>
            </Col>
          </Row>

          <Space style={{ marginBottom: 12 }}>
            <Button
              loading={loading}
              onClick={() => act("Queue swept", () => financeService.sweepPostingQueue())}
            >
              Post everything now
            </Button>
            <Text type="secondary">
              The queue is also drained automatically every minute.
            </Text>
          </Space>

          <Table
            rowKey="id"
            size="small"
            loading={loading}
            dataSource={queue?.items || []}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 900 }}
            columns={[
              {
                title: "Key",
                dataIndex: "idempotency_key",
                key: "idempotency_key",
                ellipsis: true,
                render: (v: string) => <Text code>{v}</Text>,
              },
              {
                title: "Business date",
                dataIndex: "business_date",
                key: "business_date",
                width: 130,
                render: (v: string) => (v ? String(v).slice(0, 10) : "—"),
              },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                width: 110,
                render: (v: string) => (
                  <Tag
                    color={
                      v === "posted" ? "green" : v === "failed" ? "red" : v === "pending" ? "gold" : "default"
                    }
                  >
                    {v}
                  </Tag>
                ),
              },
              { title: "Attempts", dataIndex: "attempts", key: "attempts", width: 90, align: "right" as const },
              {
                title: "Last error",
                dataIndex: "last_error",
                key: "last_error",
                ellipsis: true,
                render: (v: string | null) => v || "—",
              },
            ]}
          />
        </>
      ),
    },
    {
      key: "restaurant",
      label: "Restaurant",
      children: (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            title="Two funnels, recognised at different moments"
            description="A booking-linked POS order is charged to the guest folio and recognised when the charge is made (accrual) — the Front office cutover already covers it. A walk-in POS order or a walk-in laundry order settles straight to the ledger and is recognised when it is paid (cash) — only those are gated by the date below. Nothing before it is back-filled, so a gap is never reported as a variance. The channel decides the income account: dine-in 4300, room service 4310, takeaway 4320, laundry 4200."
          />

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={6}>
              <Card size="small" title="Walk-in ledger cutover" loading={loading}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <DatePicker
                    style={{ width: "100%" }}
                    disabled={!canManage}
                    value={restaurant ? dayjs(restaurant.cutover_date) : null}
                    onChange={saveRestaurantCutover}
                    allowClear={false}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {restaurant
                      ? `${restaurant.closed_days} business date(s) closed since the cutover`
                      : ""}
                  </Text>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card size="small" title="Reporting starts" loading={loading}>
                <Text strong style={{ fontSize: 24 }}>
                  {restaurant?.reporting_start ?? "—"}
                </Text>
                <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                  the later of the front-office and walk-in cutover dates
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card size="small" title="Waiting to post" loading={loading}>
                <Statistic
                  value={queue?.pending ?? restaurant?.pending_postings ?? 0}
                  valueStyle={{ color: (queue?.pending ?? 0) > 0 ? "#faad14" : undefined }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  deferred behind a closed business date
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card size="small" title="Held back" loading={loading}>
                <Statistic
                  value={queue?.failed ?? restaurant?.failed_postings ?? 0}
                  valueStyle={{ color: (queue?.failed ?? 0) > 0 ? "#a5171f" : undefined }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  tender with no cash / bank / card account mapping
                </Text>
              </Card>
            </Col>
          </Row>

          <Space style={{ marginBottom: 12 }}>
            <Button
              loading={loading}
              onClick={() => act("Queue swept", () => financeService.sweepPostingQueue())}
            >
              Post everything now
            </Button>
            <Text type="secondary">
              One queue serves both funnels; the full list is on the Front office tab, and it
              drains automatically every minute.
            </Text>
          </Space>
        </>
      ),
    },
    {
      key: "inventory",
      label: "Inventory",
      children: (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            title="The shelf posts by itself; people do not"
            description="Approving a purchase invoice receives stock (Dr 1200 + 1040, Cr 2010 AP or cash). Settling an order issues its recipe — a booking-linked order at the moment it is charged to the folio, a walk-in order when it is paid — so food and beverage cost lands in the same channel as the revenue it made. Waste and stock counts are recorded by a person, so they refuse a closed business date instead of slipping into it; automatic issues queue behind the close and drain a minute later. Nothing can debit 1200 by declaring itself: only a purchase invoice is allowed to."
          />

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={6}>
              <Card size="small" title="Inventory cutover" loading={loading}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <DatePicker
                    style={{ width: "100%" }}
                    disabled={!canManage}
                    value={inventory ? dayjs(inventory.cutover_date) : null}
                    onChange={saveInventoryCutover}
                    allowClear={false}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    nothing before this date is back-filled
                  </Text>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card size="small" title="Reporting starts" loading={loading}>
                <Text strong style={{ fontSize: 24 }}>
                  {inventory?.reporting_start ?? "—"}
                </Text>
                <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                  the latest of the front-office, walk-in and inventory cutovers
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card size="small" title="Waiting to post" loading={loading}>
                <Statistic
                  value={inventory?.pending_postings ?? 0}
                  valueStyle={{ color: (inventory?.pending_postings ?? 0) > 0 ? "#faad14" : undefined }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  recipe issues deferred behind a closed business date
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={6}>
              <Card size="small" title="Held back" loading={loading}>
                <Statistic
                  value={inventory?.failed_postings ?? 0}
                  valueStyle={{ color: (inventory?.failed_postings ?? 0) > 0 ? "#a5171f" : undefined }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  posts refused for a reason — they are never retried silently
                </Text>
              </Card>
            </Col>
          </Row>

          <Space style={{ marginBottom: 12 }} wrap>
            <Button onClick={() => navigate("/finance/inventory")}>Stock, recipes & counts</Button>
            <Button onClick={() => navigate("/finance/purchases")}>Purchase invoices</Button>
            <Button type="link" onClick={() => navigate("/reports")}>
              Stock valuation report
            </Button>
          </Space>
          <Text type="secondary" style={{ display: "block" }}>
            Accounts in play: 1200 Inventory, 1040 VAT input, 2010 suppliers, 5100 food cost,
            5200 beverage cost, 5300 laundry materials, 5400 other direct costs.
          </Text>
        </>
      ),
    },
    ...(seesPayroll
      ? [
          {
            key: "payroll",
            label: "Payroll",
            children: (
              <>
                <Alert
                  type="info"
                  showIcon
                  title="The rates decide what a future run withholds"
                  description="EPF and ETF are percentages of total monthly earnings — basic plus allowances, with no floor and no ceiling. APIT is withheld on the CUMULATIVE year, so the bands below are read as a running total, not per month. These settings apply from the next run onward; a run already posted is not retroactively re-costed."
                  style={{ marginBottom: 16 }}
                />

                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} md={6}>
                    <Card size="small" title="Employee EPF" loading={!payroll}>
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        max={100}
                        step={0.5}
                        addonAfter="%"
                        disabled={!canPayroll}
                        value={round2(rateEe * 100)}
                        onChange={(v) => setRateEe((Number(v) || 0) / 100)}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        deducted from the employee's gross
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} md={6}>
                    <Card size="small" title="Employer EPF" loading={!payroll}>
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        max={100}
                        step={0.5}
                        addonAfter="%"
                        disabled={!canPayroll}
                        value={round2(rateEr * 100)}
                        onChange={(v) => setRateEr((Number(v) || 0) / 100)}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        part of the employer cost, never withheld from pay
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} md={6}>
                    <Card size="small" title="Employer ETF" loading={!payroll}>
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        max={100}
                        step={0.5}
                        addonAfter="%"
                        disabled={!canPayroll}
                        value={round2(rateEtf * 100)}
                        onChange={(v) => setRateEtf((Number(v) || 0) / 100)}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        payable to the ETF board
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} md={6}>
                    <Card size="small" title="APIT bands" loading={!payroll}>
                      <Statistic
                        title="top band"
                        value={
                          payrollBands.length
                            ? round2((payrollBands[payrollBands.length - 1].rate || 0) * 100)
                            : 0
                        }
                        precision={0}
                        suffix="%"
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        IRD Tax Table No. 1
                      </Text>
                    </Card>
                  </Col>
                </Row>

                <Card
                  size="small"
                  title="IRD Tax Table No. 1 — APIT bands"
                  style={{ marginBottom: 16 }}
                  loading={!payroll}
                >
                  <Table
                    rowKey={(_, i) => String(i)}
                    size="small"
                    loading={!payroll}
                    dataSource={payrollBands}
                    pagination={false}
                    columns={[
                      {
                        title: "From",
                        key: "from",
                        width: 150,
                        align: "right" as const,
                        render: (_: any, _row: any, i: number) =>
                          formatLkr(derivedBands[i].from),
                      },
                      {
                        title: "Up to",
                        key: "max",
                        width: 200,
                        align: "right" as const,
                        render: (_: any, _row: any, i: number) => {
                          const last = i === payrollBands.length - 1;
                          if (last) return <Tag>open-ended</Tag>;
                          return (
                            <InputNumber
                              size="small"
                              style={{ width: 170 }}
                              min={derivedBands[i].from + 1}
                              disabled={!canPayroll}
                              value={payrollBands[i].max}
                              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                              onChange={(v) =>
                                setPayrollBands((prev) =>
                                  prev.map((b, j) =>
                                    j === i ? { ...b, max: v === null ? null : Number(v) } : b,
                                  ),
                                )
                              }
                            />
                          );
                        },
                      },
                      {
                        title: "Rate",
                        key: "rate",
                        width: 140,
                        align: "right" as const,
                        render: (_: any, _row: any, i: number) => (
                          <InputNumber
                            size="small"
                            style={{ width: 110 }}
                            min={0}
                            max={100}
                            step={1}
                            addonAfter="%"
                            disabled={!canPayroll}
                            value={round2((payrollBands[i].rate || 0) * 100)}
                            onChange={(v) =>
                              setPayrollBands((prev) =>
                                prev.map((b, j) =>
                                  j === i ? { ...b, rate: (Number(v) || 0) / 100 } : b,
                                ),
                              )
                            }
                          />
                        ),
                      },
                      {
                        title: "Constant",
                        key: "constant",
                        align: "right" as const,
                        render: (_: any, _row: any, i: number) => (
                          <Text type="secondary">
                            {formatLkr(derivedBands[i].constant)}
                          </Text>
                        ),
                      },
                    ]}
                  />
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 12 }}>
                    Only two columns are editable. The lower bound of a band is the upper bound of
                    the band beneath it, and the constant is the tax the bands beneath have already
                    charged — both follow from the thresholds, so typing a rate cannot leave them
                    disagreeing with each other.
                  </Text>
                </Card>

                <Space style={{ marginBottom: 12 }} wrap>
                  <Button
                    type="primary"
                    loading={savingPayroll}
                    disabled={!canPayroll || !payroll}
                    onClick={savePayroll}
                  >
                    Save statutory settings
                  </Button>
                  <Button onClick={() => navigate("/finance/payroll")}>Go to payroll</Button>
                </Space>
                <Text type="secondary" style={{ display: "block" }}>
                  Accounts in play: 6100 Salaries &amp; Wages, 2030 EPF/ETF payable, 2040 income
                  tax payable.
                </Text>
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginTop: 0 }}>
        Finance Setup
      </Title>
      {!canManage && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="Read-only"
          description="You can view the setup but only finance:accounts holders can change it."
        />
      )}
      {error && (
        <Alert type="error" showIcon title="Failed to load" description={error} style={{ marginBottom: 16 }} />
      )}

      <Tabs items={tabItems} />

      {/* ---- Ledger account ---- */}
      <LedgerAccountModal
        open={accountModal.open}
        editing={accountModal.editing}
        accounts={accounts}
        onClose={() => setAccountModal({ open: false })}
        onSaved={() => {
          void load();
        }}
      />

      {/* ---- Department ---- */}
      <Modal
        open={departmentModal}
        title="New department"
        onCancel={() => setDepartmentModal(false)}
        onOk={submitDepartment}
        okText="Create"
        destroyOnClose
      >
        <Form
          form={departmentForm}
          layout="vertical"
          key="new-department"
          initialValues={{ sort_order: departments.length + 1 }}
        >
          <Form.Item name="code" label="Code" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="security" maxLength={30} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="Security" />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort order">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---- Cash / bank account ---- */}
      <Modal
        open={bankModal.open}
        title={bankModal.editing ? `Edit ${bankModal.editing.name}` : "New cash / bank account"}
        onCancel={() => setBankModal({ open: false })}
        onOk={submitBank}
        okText={bankModal.editing ? "Save" : "Create"}
        destroyOnClose
      >
        <Form
          form={bankForm}
          layout="vertical"
          key={bankModal.editing?.id || "new-bank"}
          initialValues={
            bankModal.editing ? { ...bankModal.editing } : { type: "cash", opening_balance: 0, is_default: false }
          }
        >
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: "cash", label: "Cash" },
                    { value: "bank", label: "Bank" },
                    { value: "card", label: "Card" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                <Input placeholder="Petty cash" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="account_id" label="Ledger account" rules={[{ required: true, message: "Required" }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Pick the asset account"
              options={accounts
                .filter((a) => a.type === "asset" && a.is_posting)
                .map((a) => ({ value: a.id, label: `${a.code} — ${a.name}` }))}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="account_no" label="Account number">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="opening_balance" label="Opening balance">
                <InputNumber style={{ width: "100%" }} step={100} precision={2} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="is_default" label="Default" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ---- Party ---- */}
      <Modal
        open={partyModal.open}
        title={partyModal.editing ? `Edit ${partyModal.editing.name}` : "New customer / supplier"}
        onCancel={() => setPartyModal({ open: false })}
        onOk={submitParty}
        okText={partyModal.editing ? "Save" : "Create"}
        destroyOnClose
      >
        <Form
          form={partyForm}
          layout="vertical"
          key={partyModal.editing?.id || "new-party"}
          initialValues={partyModal.editing ? { ...partyModal.editing } : { type: "supplier" }}
        >
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: "customer", label: "Customer" },
                    { value: "supplier", label: "Supplier" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                <Input placeholder="Ceylon Beverages (Pvt) Ltd" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="contact" label="Contact person">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="email" label="Email">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="Address">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---- Approval thresholds ---- */}
      <Modal
        open={thresholdModal}
        title="Approval thresholds"
        onCancel={() => setThresholdModal(false)}
        onOk={submitThresholds}
        okText="Save"
        destroyOnClose
      >
        <Form
          form={thresholdForm}
          layout="vertical"
          key={`thresholds-${thresholds.map((t) => `${t.max}-${t.approvals}`).join("_")}`}
          initialValues={{ rows: thresholds }}
        >
          <Form.List name="rows">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Space key={field.key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
                    <Form.Item
                      name={[field.name, "max"]}
                      label={index === 0 ? "Up to (LKR)" : "Up to"}
                      rules={[{ required: index !== fields.length - 1, message: "Required" }]}
                    >
                      <InputNumber
                        style={{ width: 160 }}
                        min={1}
                        step={1000}
                        disabled={index === fields.length - 1}
                        placeholder={index === fields.length - 1 ? "No upper limit" : "e.g. 10000"}
                      />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "approvals"]}
                      label="Approvals"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <InputNumber style={{ width: 110 }} min={1} max={10} />
                    </Form.Item>
                    {index > 0 && (
                      <Button type="text" danger onClick={() => remove(field.name)}>
                        Remove
                      </Button>
                    )}
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ max: null, approvals: 1 })} block icon={<PlusOutlined />}>
                  Add band
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default FinanceSettingsPage;
